import React from 'react';
import { trpcSwr } from '../../../../../api/trpc';
import Table, { CellProps } from 'rsuite/Table';
import { ModalsService } from '../../../../../services/modals.service';
import { EditCustomsDeliveryOrderInvoiceModal } from './edit-customs-delivery-order-invoice-modal';
import moment from 'moment';
import {
  CleanCustomsDeliveryOrderInvoice,
  RbacObject,
} from '../../../../../export-types/cleaned-types';
import { toMoney } from '../../../../../utils/common.utils';
import Whisper from 'rsuite/Whisper';
import IconButton from 'rsuite/IconButton';
import { WhisperSpeaker } from '../../../../../utils/whisper-speaker';
import Popover from 'rsuite/Popover';
import Dropdown from 'rsuite/Dropdown';
import { AlertService } from '../../../../../services/alert.service';
import { Rbac } from '../../../../common/Rbac';
import { ConfirmModal } from '../../../../common/confirm-modal';
import PngInvoceForPayment from '../../../../../assets/invoce-for-payment.png';
import { InvoceForPaymentModal } from '../../../finance/invoce-for-payment.modal.component';

type Props = {
  id: string;
};

export const InvoiceForPaymentCell = ({
  rowData,
  ...props
}: CellProps<CleanCustomsDeliveryOrderInvoice>) => {
  if (!rowData) return null;

  const handleClick = async () => {
    await ModalsService.createModal(InvoceForPaymentModal, {
      url: `/api/customs-delivery/invoice/admin/${rowData.uuid}/download`,
    });
  };

  return (
    <Table.Cell {...props} className="no-padding-cell pt-2 ps-2">
      <div className="cursor-pointer" onClick={handleClick}>
        <img
          className="rounded fit-cover"
          src={PngInvoceForPayment}
          height={32}
        />
      </div>
    </Table.Cell>
  );
};

const CustomsDeliveryOrderInvoicesBlock = ({ id }: Props) => {
  const { data, isLoading, mutate } =
    trpcSwr.customsDeliveryInvoice.getInvoiceList.useSWR({
      orderId: id,
    });

  const { trigger: updateInvoice } =
    trpcSwr.customsDeliveryInvoice.updateInvoice.useSWRMutation();
  const { trigger: deleteInvoice } =
    trpcSwr.customsDeliveryInvoice.deleteInvoice.useSWRMutation();
  const { trigger: sendMessage } =
    trpcSwr.customsDeliveryInvoice.sendInvoiceMessageToClient.useSWRMutation({
      onSuccess: () => {
        AlertService.success();
      },
      onError: (e) => {
        AlertService.error(e.message);
      },
    });

  const renderMenu =
    (invoice: CleanCustomsDeliveryOrderInvoice): WhisperSpeaker =>
    ({ onClose, left, top, className }, ref) => {
      const handleSelect = (eventKey: number | string | undefined) => {
        switch (eventKey) {
          case 1: {
            ModalsService.createModal(EditCustomsDeliveryOrderInvoiceModal, {
              defaultValues: invoice,
              orderId: id,
            }).then(() => {
              mutate();
            });
            break;
          }
          case 2: {
            onClose();
            ModalsService.createModal(ConfirmModal, {
              title: 'Удалить счёт?',
              buttonText: 'Удалить',
            }).then(async () => {
              await deleteInvoice({ invoiceId: invoice.uuid });
              AlertService.success();
              mutate();
            });
            break;
          }
          case 3: {
            navigator.clipboard
              .writeText(
                `https://my.express-today.ru/customs-deliveries/invoice/${invoice.uuid}`,
              )
              .then(() => {
                AlertService.success();
              });
            break;
          }
          case 4: {
            sendMessage({ orderId: id, invoiceId: invoice.uuid });
            break;
          }
          default:
            return;
        }
      };
      return (
        <Popover ref={ref} className={className} style={{ left, top }} full>
          <Dropdown.Menu onSelect={handleSelect}>
            <Dropdown.Item eventKey={1}>
              <i className="bi bi-pencil" />
              <span className="ps-2">Изменить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={2}>
              <i className="bi bi-trash" />
              <span className="ps-2">Удалить</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={3}>
              <i className="bi bi-clipboard" />
              <span className="ps-2">Скопировать ссылку</span>
            </Dropdown.Item>
            <Dropdown.Item eventKey={4}>
              <i className="bi bi-messenger" />
              <span className="ps-2">Отправить клиенту</span>
            </Dropdown.Item>
          </Dropdown.Menu>
        </Popover>
      );
    };

  const CheckedCell = ({
    rowData,
    ...props
  }: CellProps<CleanCustomsDeliveryOrderInvoice>) => {
    const checkInvoice = (
      event: React.MouseEvent<HTMLDivElement>,
      invoice: CleanCustomsDeliveryOrderInvoice,
    ) => {
      const result = window.confirm(
        'Вы действительно хотите подтвердить платёж?',
      );
      if (!result) {
        event.preventDefault();
        return;
      }
      updateInvoice({
        invoiceId: invoice.uuid,
        data: {
          isPaid: true,
        },
      }).then(() => {
        AlertService.success();
        mutate();
      });
    };

    return (
      rowData && (
        <Rbac
          object={RbacObject.CustomsDelivery}
          action={'write'}
          children={
            <Table.Cell {...props} align="center">
              <div
                className="form-check text-center d-inline-block"
                onClick={(event) => checkInvoice(event, rowData)}
                title={
                  rowData.isPaid ? 'Платёж подтверждён' : 'Подтвердить платёж'
                }
              >
                <input
                  className="form-check-input p-2 border-2 cursor-pointer"
                  type="checkbox"
                  disabled={rowData.isPaid}
                  defaultChecked={rowData.isPaid}
                  onClick={() => {}}
                />
              </div>
            </Table.Cell>
          }
          otherwise={() => (
            <Table.Cell {...props} align="center">
              <div
                className="form-check text-center d-inline-block"
                title={
                  rowData?.isPaid
                    ? 'Платёж подтверждён'
                    : 'Платёж не подтверждён'
                }
              >
                <input
                  className="form-check-input p-2 border-2 cursor-pointer"
                  type="checkbox"
                  disabled
                  defaultChecked={rowData?.isPaid}
                />
              </div>
            </Table.Cell>
          )}
        />
      )
    );
  };

  const ActionCell = ({
    rowData,
    dataKey,
    ...props
  }: CellProps<CleanCustomsDeliveryOrderInvoice>) => {
    return (
      rowData &&
      !rowData.isPaid && (
        <Table.Cell
          {...props}
          className="link-group no-padding-cell pt-2"
          align="right"
        >
          <Whisper
            placement="leftStart"
            trigger="click"
            speaker={renderMenu(rowData)}
          >
            <IconButton
              appearance="subtle"
              icon={<i className="bi bi-three-dots" />}
            />
          </Whisper>
        </Table.Cell>
      )
    );
  };

  return (
    <div>
      <div className="d-flex flex-row justify-content-between">
        <div className="fs-18 fw-600 color-gray-450">Счета клиента</div>
        <div
          className={'btn btn-success'}
          onClick={async () => {
            await ModalsService.createModal(
              EditCustomsDeliveryOrderInvoiceModal,
              {
                orderId: id,
              },
            );
            mutate();
          }}
        >
          Создать счёт
        </div>
      </div>
      <Table
        data={data}
        headerHeight={57}
        autoHeight
        loading={isLoading}
        locale={{ emptyMessage: 'Пусто' }}
      >
        <Table.Column width={60} fullText>
          <Table.HeaderCell>№</Table.HeaderCell>
          <Table.Cell dataKey="id" />
        </Table.Column>
        <Table.Column flexGrow={1} fullText>
          <Table.HeaderCell>Дата создания</Table.HeaderCell>
          <Table.Cell>
            {(rowData: CleanCustomsDeliveryOrderInvoice) =>
              moment(rowData.createdAt).format('DD.MM.YYYY')
            }
          </Table.Cell>
        </Table.Column>
        <Table.Column flexGrow={1} fullText>
          <Table.HeaderCell> Сумма пополнения</Table.HeaderCell>
          <Table.Cell>
            {(rowData: CleanCustomsDeliveryOrderInvoice) =>
              toMoney(
                (rowData.goods || []).reduce((acc, current) => {
                  return acc + current.price * current.amount;
                }, 0),
                'rub',
              )
            }
          </Table.Cell>
        </Table.Column>
        <Table.Column fullText>
          <Table.HeaderCell> Оплачен</Table.HeaderCell>
          <CheckedCell />
        </Table.Column>
        <Table.Column flexGrow={1} fullText>
          <Table.HeaderCell> Счет</Table.HeaderCell>
          <InvoiceForPaymentCell />
        </Table.Column>
        <Table.Column>
          <Table.HeaderCell> </Table.HeaderCell>
          <ActionCell />
        </Table.Column>
      </Table>
    </div>
  );
};

export default CustomsDeliveryOrderInvoicesBlock;
